import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Collapse,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Menu,
  Checkbox,
  styled,
  Typography,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Settings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import dayjs from 'dayjs';
import useDebounce from '../../hooks/debounceHook';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { useView } from '../../utils/PersonalViewProvider';
import {
  formatFloatToTimeHM,
  parseTimeHMToFloat,
  DEFAULT_TIME_LOG_MINUTES,
  COMPACT_DATE_TIME_FORMAT,
} from '../../utils/timeFormatter';
import NoteBasedTimeLog from './NoteBasedTimeLog';
import TrackerTimeLog from './TrackerTimeLog';
import CompactDateTimePicker from './CompactDateTimePicker';

/**
 * @typedef {Object} TimeLogEntry
 * @property {number|null} id - Time log ID
 * @property {string} name - Task name
 * @property {string} category - Task category
 * @property {string} status - Task status
 * @property {number} line_up - Task priority
 * @property {string} do_date - Task due date
 * @property {Object} assignee - Assigned user
 * @property {string} track - Track name
 * @property {string} project - Project name
 * @property {number} workspace - Workspace ID
 * @property {string} total_duration - Total duration
 * @property {number} time_log_count - Time log count
 * @property {string} start_time - Start time
 * @property {string} end_time - End time
 * @property {string} action_item - Action item description
 * @property {string} meeting - Meeting name
 * @property {number} attendee - Attendee ID
 * @property {string} duration - Duration
 * @property {string} notes - Notes
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Update timestamp
 */

/**
 * @typedef {Object} ActionItem
 * @property {number} id - Action item ID
 * @property {string} description - Action item description
 */

/**
 * @typedef {Object} Task
 * @property {number} task_id - Task ID
 * @property {string} task_name - Task name
 * @property {ActionItem[]} action_items - Array of action items
 */

/**
 * @typedef {Object} Meeting
 * @property {number} id - Meeting ID
 * @property {string} meeting_name - Meeting name
 */

/**
 * @typedef {Object} Project
 * @property {number} id - Project ID
 * @property {string} name - Project name
 */

/**
 * @typedef {Object} Track
 * @property {number} id - Track ID
 * @property {string} name - Track name
 */

/**
 * @typedef {Object} Epic
 * @property {number} id - Epic ID
 * @property {string} name - Epic name
 */

/**
 * @typedef {Object} Column
 * @property {string} id - Column identifier
 * @property {string} label - Column display label
 */

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-head': {
    fontWeight: 'bold',
  },
  '& .MuiTableCell-root': {
    padding: '5px 10px',
    borderBottom: 'none',
  },
  '& .MuiTableRow-root': {
    borderBottom: 'none',
  },
}));

const NotesTableCell = styled(TableCell)({
  minWidth: '30%',
  maxWidth: '40%',
});

const DurationTableCell = styled(TableCell)({
  width: 120,
});

const TimeTableCell = styled(TableCell)({
  width: 100,
});

// Room for "Sep 24, 10:00" plus the picker's calendar button.
const TimePickerTableCell = styled(TableCell)({
  width: 190,
  minWidth: 190,
});

const ActionItemTableCell = styled(TableCell)({
  minWidth: 220,
  maxWidth: 360,
});

/**
 * Read-only text clamped to one line; the toggle (shown only when the text is
 * cut off) expands it to the full wrapped text.
 * @param {Object} props
 * @param {string} props.text
 */
const ExpandableText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const textRef = useRef(null);

  // Re-measure when the text or the column width changes.
  useEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return undefined;
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  const canToggle = expanded || truncated;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
      <Typography
        ref={textRef}
        variant="body2"
        title={expanded ? undefined : text}
        onClick={canToggle ? () => setExpanded((v) => !v) : undefined}
        sx={{
          flex: 1,
          minWidth: 0,
          py: 0.5,
          cursor: canToggle ? 'pointer' : 'default',
          ...(expanded
            ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
            : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
        }}
      >
        {text}
      </Typography>
      {canToggle && (
        <IconButton
          size="small"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      )}
    </Box>
  );
};

/**
 * Time log management component with task-based, action item-based and
 * note-based (AI-linked) views
 * @component
 * @returns {React.ReactElement} The time log management interface
 */
const TimeLog = () => {
  const [timeLogs, setTimeLogs] = useState([]);
  const [pendingUpdates, setPendingUpdates] = useState({});
  // Unsaved rows have no id yet, so they are keyed by a temp id. Tracks which
  // temp rows currently have a POST in flight so we never create duplicates.
  const nextTempId = useRef(1);
  const inFlightCreates = useRef(new Set());
  const [anchorEl, setAnchorEl] = useState(null);
  
  const [taskBasedVisibleColumns, setTaskBasedVisibleColumns] = useState([
    'open',
    'name',
    'total_duration',
    'start_time',
    'end_time',
  ]);
  
  const [actionItemBasedVisibleColumns, setActionItemBasedVisibleColumns] = useState([
    'open',
    'action_item',
    'start_time',
    'end_time',
    'duration',
    'notes',
  ]);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [meetings, setMeetings] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [expandedTasks, setExpandedTasks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [projects, setProjects] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [dialogTracks, setDialogTracks] = useState([]);
  const [epics, setEpics] = useState([]);
  // Scope for the "Select Action Item" dialog. Kept apart from the page-level
  // epic/project/track filters: they used to share `epicFilter`, so narrowing
  // the picker also re-filtered the table behind it.
  const [dialogEpic, setDialogEpic] = useState('');
  const [dialogProjects, setDialogProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTrack, setSelectedTrack] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  // Combine every workspace the user can see instead of just the current one.
  const [allWorkspaces, setAllWorkspaces] = useState(false);
  const [epicFilter, setEpicFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [trackFilter, setTrackFilter] = useState('');
  // 0 = task based, 1 = action item based, 2 = note based. The action item tab
  // is shown first and is the default.
  const [currentTab, setCurrentTab] = useState(1);
  const [apiEndpoint, setApiEndpoint] = useState('time-logs/');

  const { selectedAssignee } = useView();
  const { currentWorkspace, workspaces, selectWorkspace } = useWorkspace();
  const navigate = useNavigate();

  const taskBasedColumns = [
    { id: 'open', label: '' },
    { id: 'id', label: 'ID' },
    { id: 'name', label: 'Name' },
    { id: 'category', label: 'Category' },
    { id: 'status', label: 'Status' },
    { id: 'line_up', label: 'Line Up' },
    { id: 'do_date', label: 'Do Date' },
    { id: 'assignee', label: 'Assignee' },
    { id: 'track', label: 'Track' },
    { id: 'project', label: 'Project' },
    { id: 'workspace', label: 'Workspace' },
    { id: 'total_duration', label: 'Total Duration' },
    { id: 'time_log_count', label: 'Time Log Count' },
    { id: 'start_time', label: 'Start Time' },
    { id: 'end_time', label: 'End Time' },
    { id: 'created_at', label: 'Created At' },
    { id: 'updated_at', label: 'Updated At' },
  ];

  const actionItemBasedColumns = [
    { id: 'open', label: '' },
    { id: 'id', label: 'ID' },
    { id: 'workspace', label: 'Workspace' },
    { id: 'project', label: 'Project' },
    { id: 'track', label: 'Track' },
    { id: 'task', label: 'Task' },
    { id: 'action_item', label: 'Action Item' },
    { id: 'meeting', label: 'Meeting' },
    { id: 'attendee', label: 'Attendee' },
    { id: 'start_time', label: 'Start Time' },
    { id: 'end_time', label: 'End Time' },
    { id: 'duration', label: 'Duration' },
    { id: 'notes', label: 'Notes' },
    { id: 'created_at', label: 'Created At' },
    { id: 'updated_at', label: 'Updated At' },
  ];

  const allColumns = currentTab === 0 ? taskBasedColumns : actionItemBasedColumns;
  const chosenColumns = currentTab === 0 ? taskBasedVisibleColumns : actionItemBasedVisibleColumns;
  // Rows from several workspaces need the workspace shown to tell them apart.
  const visibleColumns =
    allWorkspaces && !chosenColumns.includes('workspace')
      ? [...chosenColumns, 'workspace']
      : chosenColumns;
  const setVisibleColumns = currentTab === 0 ? setTaskBasedVisibleColumns : setActionItemBasedVisibleColumns;

  /**
   * Opens the column selection menu
   * @param {React.MouseEvent} event - Click event
   */
  const handleColumnMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Closes the column selection menu
   */
  const handleColumnMenuClose = () => {
    setAnchorEl(null);
  };

  /**
   * Fetches time logs from the API
   * @async
   * @returns {Promise<void>}
   */
  const fetchTimeLogs = async () => {
    try {
      const params = allWorkspaces
        ? { all_workspaces: true }
        : { workspace_id: currentWorkspace?.id };
      
      if (selectedAssignee) {
        params.assignee = selectedAssignee;
      }
      if (dateFilter === 'custom') {
        if (rangeStart) params.start_date = rangeStart;
        if (rangeEnd) params.end_date = rangeEnd;
      }

      const response = await api.get(`/${apiEndpoint}`, { params });
      const dataArray = response.data.payload || response.data || [];
      setTimeLogs(Array.isArray(dataArray) ? dataArray : []);
    } catch (error) {
      console.error('Error fetching time logs:', error);
      setTimeLogs([]);
    }
  };

  useEffect(() => {
    if (currentWorkspace?.id) {
      api.get('/epics/', { params: { workspace_id: currentWorkspace.id } }).then((response) => {
        const epicsData = response.data.payload || response.data || [];
        setEpics(epicsData);
      });

      const projectParams = { workspace_id: currentWorkspace.id };
      const projectEndpoint = '/filtered-projects/';
      
      if (epicFilter) projectParams.epic_id = epicFilter;

      api.get(projectEndpoint, { params: projectParams }).then((response) => {
        const projectsData = response.data.payload || response.data || [];
        setProjects(projectsData);
      });

      if (!projectFilter) {
        setTracks([]);
      }
    }
  }, [currentWorkspace?.id, projectFilter, epicFilter]);

  // Load tracks based on selected project
  useEffect(() => {
    if (projectFilter && currentWorkspace?.id) {
      // Find the project object to get its ID
      const selectedProjectObj = projects.find(p => p.name === projectFilter);
      if (selectedProjectObj) {
        const params = {
          workspace_id: currentWorkspace.id,
          project_id: selectedProjectObj.id,
        };

        api.get(`/filtered-tracks/`, { params }).then((response) => {
          const tracksData = response.data.payload || response.data || [];
          setTracks(tracksData);
        }).catch((error) => {
          console.error('Error fetching tracks for project:', error);
          setTracks([]); // Clear tracks if error
        });
      }
    } else if (!projectFilter) {
      // Clear tracks when no project is selected
      setTracks([]);
    }
  }, [projectFilter, projects, currentWorkspace?.id])

  // Projects for the dialog, narrowed by the dialog's own epic.
  useEffect(() => {
    if (!openDialog || dialogType !== 'action_item' || !currentWorkspace?.id) return;
    const params = { workspace_id: currentWorkspace.id };
    if (dialogEpic) params.epic_id = dialogEpic;
    api
      .get('/filtered-projects/', { params })
      .then((response) => setDialogProjects(response.data.payload || response.data || []))
      .catch((error) => {
        console.error('Error fetching dialog projects:', error);
        setDialogProjects([]);
      });
  }, [openDialog, dialogType, dialogEpic, currentWorkspace?.id]);

  // Load tracks for dialog based on selected project in dialog
  useEffect(() => {
    if (selectedProject && currentWorkspace?.id) {
      const params = {
        workspace_id: currentWorkspace.id,
        project_id: selectedProject,
      };

      api.get(`/filtered-tracks/`, { params }).then((response) => {
        const tracksData = response.data.payload || response.data || [];
        setDialogTracks(tracksData);
      }).catch((error) => {
        console.error('Error fetching dialog tracks for project:', error);
        setDialogTracks([]); // Clear tracks if error
      });
    } else {
      // Clear tracks when no project is selected in dialog
      setDialogTracks([]);
    }
  }, [selectedProject, currentWorkspace?.id])
  
  // currentTab is a dependency so returning from the Note Based or Tracker tab
  // refetches: both create time logs this table hasn't loaded yet.
  useEffect(() => {
    if (currentWorkspace?.id && currentTab !== 2 && currentTab !== 3) {
      fetchTimeLogs();
    }
  }, [currentWorkspace, selectedAssignee, apiEndpoint, allWorkspaces, dateFilter, rangeStart, rangeEnd, currentTab]);

  /**
   * Jumps to the task (and action item) behind a row. Task details load from
   * the current workspace, so switch first when the row belongs to another one.
   * @param {TimeLogEntry} log - Row to open
   */
  const handleOpenTask = (log) => {
    const taskId = currentTab === 0 ? log.id : log.task_id;
    if (!taskId) return;
    if (log.workspace && log.workspace !== currentWorkspace?.id) {
      const target = workspaces.find((ws) => ws.id === log.workspace);
      if (target) selectWorkspace(target);
    }
    const query = currentTab === 1 && log.action_item_id ? `?action_item=${log.action_item_id}` : '';
    navigate(`/tasks/${taskId}${query}`);
  };

  /**
   * Handles tab change between task-based and action item-based views
   * @param {React.SyntheticEvent} event - Tab change event
   * @param {number} newValue - New tab index
   */
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    // The note based and tracker tabs load their own data.
    if (newValue !== 2 && newValue !== 3) setApiEndpoint(newValue === 0 ? 'task-time-logs/' : 'time-logs/');
  };

  /**
   * Adds a new time log row
   */
  const handleAddRow = () => {
    // Only the Action Item tab takes new rows; the Task tab is a read-only roll-up.
    if (currentTab !== 1) return;
    // Starts now and ends DEFAULT_TIME_LOG_MINUTES later; moving either end
    // re-derives the duration.
    const start = dayjs().second(0).millisecond(0);
    const newRow = {
      id: null,
      _tempId: `new-${nextTempId.current++}`,
      start_time: start,
      end_time: start.add(DEFAULT_TIME_LOG_MINUTES, 'minute'),
      action_item: '',
      task: '',
      track: '',
      project: '',
      meeting: null,
      attendee: localStorage.getItem('user') || 1,
      duration: (DEFAULT_TIME_LOG_MINUTES / 60).toFixed(2),
      notes: '',
      created_at: dayjs(),
      updated_at: dayjs(),
    };

    setTimeLogs([
      newRow,
      ...(Array.isArray(timeLogs) ? timeLogs : []),
    ]);
  };

  /**
   * Toggles column visibility
   * @param {string} columnId - Column identifier to toggle
   */
  const handleColumnToggle = (columnId) => {
    setVisibleColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  };

  /**
   * Debounced function to send pending updates to the API
   */
  const sendPendingUpdates = useDebounce(async () => {
    // Take a snapshot and clear immediately so edits typed while requests are
    // in flight land in a fresh batch instead of being wiped afterwards.
    const batch = pendingUpdates;
    if (Object.keys(batch).length === 0) return;
    setPendingUpdates({});

    const requeue = (key, updates) =>
      setPendingUpdates((prev) => ({
        ...prev,
        [key]: { ...updates, ...(prev[key] || {}) },
      }));
    // The server derives duration from the range; mirror it back into the row.
    const applyServerDuration = (matches, data) =>
      setTimeLogs((prevLogs) =>
        prevLogs.map((log) => (matches(log) ? { ...log, duration: data.duration } : log))
      );

    await Promise.all(
      Object.entries(batch).map(async ([key, updates]) => {
        const row = timeLogs.find((log) => String(log.id) === key || log._tempId === key);
        const isNew = key.startsWith('new-');

        try {
          if (isNew && row?.id) {
            // Row was created since these edits were queued.
            const response = await api.patch(`/time-logs/${row.id}/`, updates);
            applyServerDuration((log) => log.id === row.id, response.data);
          } else if (isNew) {
            if (inFlightCreates.current.has(key)) {
              // Wait for the POST to finish; it re-triggers the sender.
              requeue(key, updates);
              return;
            }
            inFlightCreates.current.add(key);
            try {
              const response = await api.post('/time-logs/', {
                ...updates,
                start_time: updates.start_time || row?.start_time || dayjs().format(),
                end_time: updates.end_time || row?.end_time || null,
                attendee: localStorage.getItem('user'),
              });
              setTimeLogs((prevLogs) =>
                prevLogs.map((log) =>
                  log._tempId === key
                    ? {
                        ...log,
                        ...response.data,
                        action_item: response.data.action_item_name ?? null,
                        meeting: response.data.meeting_name ?? null,
                        _tempId: key,
                      }
                    : log
                )
              );
            } finally {
              inFlightCreates.current.delete(key);
              sendPendingUpdates();
            }
          } else {
            const response = await api.patch(`/time-logs/${key}/`, updates);
            applyServerDuration((log) => String(log.id) === key, response.data);
          }
        } catch (error) {
          console.error('Error updating time logs:', error);
        }
      })
    );
  });

  /**
   * Queues a field change for a row and schedules the debounced save
   * @param {Object} log - Row being edited
   * @param {Object} fields - Field name/value pairs
   */
  const queueUpdate = (log, fields) => {
    const key = String(log.id ?? log._tempId);
    setPendingUpdates((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...fields },
    }));
    sendPendingUpdates();
  };

  /**
   * Handles input changes for time log fields
   * @param {number} index - Row index
   * @param {string} field - Field name
   * @param {*} value - New value
   */
  const handleInputChange = (index, field, value) => {
    const updated = { ...timeLogs[index], [field]: value };
    const fields = { [field]: value };

    // Duration follows the time range (mirrors the server-side rule).
    if (field === 'start_time' || field === 'end_time') {
      const start = dayjs(updated.start_time);
      const end = dayjs(updated.end_time);
      if (updated.start_time && updated.end_time && start.isValid() && end.isValid()) {
        updated.duration = Math.max(end.diff(start, 'minute') / 60, 0).toFixed(2);
      }
      // Send both ends so a PATCH always carries the full range.
      if (updated.start_time) fields.start_time = updated.start_time;
      if (updated.end_time) fields.end_time = updated.end_time;
    }

    setTimeLogs((prevLogs) => {
      const newLogs = [...prevLogs];
      newLogs[index] = updated;
      return newLogs;
    });
    queueUpdate(timeLogs[index], fields);
  };

  /**
   * Fetches action items based on current filters
   */
  const fetchActionItems = () => {
    if (!currentWorkspace?.id) return;
    const params = { workspace_id: currentWorkspace.id };
    if (selectedProject) params.project_id = selectedProject;
    if (selectedTrack) params.track_id = selectedTrack;
    api
      .get('/grouped-action-items/', { params })
      .then((response) => setActionItems(response.data.payload || []))
      .catch((error) => {
        console.error('Error fetching action items:', error);
        setActionItems([]);
      });
  };

  // Re-query whenever the picker's scope changes. Calling fetchActionItems
  // straight after setSelectedProject/Track read the stale values, so the
  // list never narrowed.
  useEffect(() => {
    if (openDialog && dialogType === 'action_item') fetchActionItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, dialogType, selectedProject, selectedTrack, currentWorkspace?.id]);

  /**
   * Opens a dialog for selecting meetings or action items
   * @param {string} type - Dialog type ('meeting' or 'action_item')
   * @param {number} index - Row index
   */
  const handleOpenDialog = (type, index) => {
    setDialogType(type);
    setSelectedIndex(index);
    setOpenDialog(true);

    if (type === 'meeting') {
      api
        .get('/meetings/')
        .then((response) => setMeetings(response.data.payload));
    }
    // Action items load from the scope effect once the dialog is open.
  };

  /**
   * Closes the dialog
   */
  const handleDialogClose = () => {
    setOpenDialog(false);
    setDialogType('');
    setSelectedIndex(null);
    setSearchQuery('');
  };

  /**
   * Updates time log state
   * @param {number} logId - Time log ID
   * @param {Object} updateData - Data to update
   */
  const updateTimeLogState = (logId, updateData) => {
    setTimeLogs((prevLogs) =>
      prevLogs.map((log) =>
        log.id === logId ? { ...log, ...updateData } : log
      )
    );
  };

  /**
   * Handles meeting selection
   * @async
   * @param {Meeting} meeting - Selected meeting
   */
  const handleMeetingSelect = async (meeting) => {
    if (selectedIndex === null) {
      handleDialogClose();
      return;
    }

    const selectedLog = timeLogs[selectedIndex];
    const logId = selectedLog.id;

    const updatedLogs = [...timeLogs];
    updatedLogs[selectedIndex] = { ...selectedLog, meeting: meeting.meeting_name };
    setTimeLogs(updatedLogs);

    if (logId) {
      try {
        const response = await api.patch(`/time-logs/${logId}/`, {
          meeting: meeting.id,
        });
        updateTimeLogState(logId, { meeting: response.data.meeting_name });
      } catch (error) {
        console.error('Failed to update meeting:', error);
      }
    } else {
      // Unsaved row: the meeting goes out with the create request.
      queueUpdate(selectedLog, { meeting: meeting.id });
    }
    handleDialogClose();
  };

  /**
   * Handles action item selection
   * @async
   * @param {ActionItem} actionItem - Selected action item
   */
  const handleActionItemSelect = async (actionItem) => {
    if (selectedIndex === null) {
      handleDialogClose();
      return;
    }

    const selectedLog = timeLogs[selectedIndex];
    const logId = selectedLog.id;

    const updatedLogs = [...timeLogs];
    updatedLogs[selectedIndex] = {
      ...selectedLog,
      action_item: actionItem.description || actionItem.name,
    };
    setTimeLogs(updatedLogs);

    if (logId) {
      try {
        const response = await api.patch(`/time-logs/${logId}/`, {
          action_item: actionItem.id,
        });
        updateTimeLogState(logId, {
          action_item: response.data.action_item_name,
        });
      } catch (error) {
        console.error('Failed to update action item:', error);
      }
    } else {
      // Unsaved row: the action item goes out with the create request.
      queueUpdate(selectedLog, { action_item: actionItem.id });
    }
    handleDialogClose();
  };

  /**
   * Toggles task expansion in action item dialog
   * @param {number} taskId - Task ID to toggle
   */
  const handleTaskToggle = (taskId) => {
    setExpandedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const filteredActionItems = actionItems?.filter(
    (task) =>
      task?.task_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task?.action_items?.some((item) =>
        item?.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  /**
   * Renders appropriate table cell component based on column type
   * @param {string} columnId - Column identifier
   * @returns {React.ComponentType} Table cell component
   */
  const renderTableCell = (columnId) => {
    if (columnId === 'open') return styled(TableCell)({ width: 40 });
    if (currentTab === 0) {
      switch (columnId) {
        case 'name':
          return NotesTableCell;
        case 'total_duration':
        case 'time_log_count':
        case 'line_up':
          return DurationTableCell;
        case 'start_time':
        case 'end_time':
        case 'do_date':
          return TimeTableCell;
        case 'assignee':
          return styled(TableCell)({ minWidth: '150px' });
        default:
          return TableCell;
      }
    } else {
      switch (columnId) {
        case 'notes':
          return NotesTableCell;
        case 'action_item':
          return ActionItemTableCell;
        case 'duration':
          return DurationTableCell;
        case 'start_time':
        case 'end_time':
          return TimePickerTableCell;
        default:
          return TableCell;
      }
    }
  };

  /**
   * Renders cell content based on column type and current tab
   * @param {TimeLogEntry} log - Time log entry
   * @param {string} columnId - Column identifier
   * @param {number} index - Row index
   * @returns {React.ReactElement} Cell content
   */
  const renderCell = (log, columnId, index) => {
    if (columnId === 'open') {
      const canOpen = currentTab === 0 ? Boolean(log.id) : Boolean(log.task_id);
      return (
        <Tooltip title={canOpen ? 'Open task' : 'No task linked'}>
          <span>
            <IconButton size="small" disabled={!canOpen} onClick={() => handleOpenTask(log)} aria-label="Open task">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      );
    }
    if (columnId === 'workspace') {
      return (
        <Typography variant="body2">{log.workspace_name || log.workspace || ''}</Typography>
      );
    }
    if (currentTab === 0) {
      // The task tab is a roll-up of action-item logs, so it is read-only;
      // entries are added and edited on the Action Item tab.
      switch (columnId) {
        case 'assignee':
          return (
            <Box>
              <Typography variant="body2">
                {log.assignee?.full_name || log.assignee?.username || ''}
              </Typography>
              {log.assignee?.email && (
                <Typography variant="caption" color="text.secondary">
                  {log.assignee.email}
                </Typography>
              )}
            </Box>
          );
        case 'start_time':
        case 'end_time':
        case 'created_at':
        case 'updated_at':
          return (
            <Typography variant="body2" color="text.secondary">
              {log[columnId] ? dayjs(log[columnId]).format(COMPACT_DATE_TIME_FORMAT) : ''}
            </Typography>
          );
        case 'total_duration':
          return (
            <Typography variant="body2">{formatFloatToTimeHM(log.total_duration)}</Typography>
          );
        default:
          return <Typography variant="body2">{log[columnId] ?? ''}</Typography>;
      }
    } else {
      switch (columnId) {
        case 'action_item':
          return log.action_item || log.meeting ? (
            <ExpandableText text={log.action_item || ''} />
          ) : (
            <Button
              variant="outlined"
              onClick={() => handleOpenDialog('action_item', index)}
            >
              Connect
            </Button>
          );
        case 'meeting':
          return log.meeting || log.action_item ? (
            <TextField
              value={log.meeting || ''}
              variant="outlined"
              fullWidth
            />
          ) : (
            <Button
              variant="outlined"
              onClick={() => handleOpenDialog('meeting', index)}
            >
              Connect
            </Button>
          );
        case 'start_time':
        case 'end_time':
          return (
            <CompactDateTimePicker
              value={log[columnId]}
              onChange={(value) => handleInputChange(index, columnId, value)}
            />
          );
        case 'duration':
          // Shown as "Xh Ym"; derived from the range once an end time is set,
          // otherwise editable (accepts "1h 30m", "1:30" or "1.5"), committed on blur.
          return (
            <TextField
              key={`${log.id ?? log._tempId}-${log.duration}`}
              defaultValue={formatFloatToTimeHM(log.duration)}
              disabled={Boolean(log.end_time)}
              onBlur={(e) => {
                const hours = parseTimeHMToFloat(e.target.value);
                if (!isNaN(hours) && hours !== parseFloat(log.duration)) {
                  handleInputChange(index, 'duration', hours);
                } else {
                  e.target.value = formatFloatToTimeHM(log.duration);
                }
              }}
              variant="outlined"
              fullWidth
            />
          );
        case 'attendee':
          return (
            <TextField
              type="number"
              value={log.attendee || ''}
              onChange={(e) =>
                handleInputChange(index, 'attendee', e.target.value)
              }
              variant="outlined"
              fullWidth
              placeholder="User ID"
            />
          );
        default:
          return (
            <TextField
              value={log[columnId] || ''}
              onChange={(e) => handleInputChange(index, columnId, e.target.value)}
              variant="outlined"
              fullWidth
            />
          );
      }
    }
  };

  /**
   * Renders dialog content based on dialog type
   * @returns {React.ReactElement|null} Dialog content
   */
  const renderDialogContent = () => {
    if (dialogType === 'action_item') {
      return (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Epic</InputLabel>
              <Select
                label="Epic"
                value={dialogEpic}
                onChange={(e) => {
                  setDialogEpic(e.target.value);
                  setSelectedProject(''); // Clear project when epic changes
                  setSelectedTrack(''); // Clear track when epic changes
                }}
              >
                <MenuItem value="">All</MenuItem>
                {epics.map((epic) => (
                  <MenuItem key={epic.id} value={epic.id}>
                    {epic.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Project</InputLabel>
              <Select
                label="Project"
                value={selectedProject}
                onChange={(e) => {
                  setSelectedProject(e.target.value);
                  setSelectedTrack(''); // Clear track when project changes
                }}
              >
                <MenuItem value="">All</MenuItem>
                {dialogProjects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Track</InputLabel>
              <Select
                label="Track"
                value={selectedTrack}
                onChange={(e) => setSelectedTrack(e.target.value)}
                disabled={!selectedProject}
              >
                <MenuItem value="">
                  {!selectedProject ? "Select project first" : "All"}
                </MenuItem>
                {dialogTracks.map((track) => (
                  <MenuItem key={track.id} value={track.id}>
                    {track.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            placeholder="Search action items..."
            variant="outlined"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
          />

          {filteredActionItems?.length === 0 ? (
            <Typography
              sx={{ p: 2, textAlign: "center", color: "text.secondary" }}
            >
              No Action Items Available
            </Typography>
          ) : (
            <List>
              {filteredActionItems.map((task) => (
                <Box key={task.task_id}>
                  <ListItemButton
                    onClick={() => handleTaskToggle(task.task_id)}
                  >
                    <ListItemText primary={task.task_name} />
                    {expandedTasks.includes(task.task_id) ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </ListItemButton>

                  <Collapse
                    in={expandedTasks.includes(task.task_id)}
                    timeout="auto"
                  >
                    <List component="div" sx={{ pl: 4 }}>
                      {task.action_items?.length ? (
                        task.action_items.map((item) => (
                          <ListItem key={item.id} disablePadding>
                            <ListItemButton
                              onClick={() => handleActionItemSelect(item)}
                            >
                              <ListItemText primary={item.description} />
                            </ListItemButton>
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText
                            secondary="No action items for this task"
                            sx={{ color: "text.secondary" }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Collapse>
                </Box>
              ))}
            </List>
          )}
        </Box>
      );
    }
    if (dialogType === 'meeting') {
      if (!meetings || meetings.length === 0) {
        return <div>Loading meetings...</div>;
      }
      return (
        <List>
          {meetings.map((meeting) => (
            <ListItem key={meeting.id} disablePadding>
              <ListItemButton onClick={() => handleMeetingSelect(meeting)}>
                <ListItemText primary={meeting.meeting_name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      );
    }
    return null;
  };

  /**
   * Filtered and processed time logs based on current filters
   */
  const filteredTimeLogs = useMemo(() => {
    if (!Array.isArray(timeLogs)) {
      return [];
    }
    
    let logs = [...timeLogs];

    // Date filter ('custom' is applied server-side via start_date/end_date)
    if (dateFilter !== 'all' && dateFilter !== 'custom') {
      logs = logs.filter((log) => {
        const logDate = dayjs(log.start_time);
        const now = dayjs();
        if (dateFilter === 'today') {
          return logDate.isSame(now, 'day');
        }
        if (dateFilter === 'yesterday') {
          return logDate.isSame(now.subtract(1, 'day'), 'day');
        }
        if (dateFilter === 'last_week') {
          const lastMonday = now.startOf('week').subtract(1, 'week').add(1, 'day');
          const lastSaturday = lastMonday.add(5, 'day').endOf('day');
          return (
            logDate.isAfter(lastMonday) ||
            logDate.isSame(lastMonday, 'day')
          ) && (
            logDate.isBefore(lastSaturday) ||
            logDate.isSame(lastSaturday, 'day')
          );
        }
        return true;
      });
    }

    // Project filter
    if (projectFilter) {
      logs = logs.filter((log) => String(log.project) === String(projectFilter));
    }

    // Track filter
    if (trackFilter) {
      logs = logs.filter((log) => String(log.track) === String(trackFilter));
    }

    return logs;
  }, [timeLogs, dateFilter, projectFilter, trackFilter]);

  /**
   * Calculates total duration from filtered time logs
   */
  const totalDuration = useMemo(() => {
    return filteredTimeLogs.reduce((sum, log) => {
      const durationField = currentTab === 0 ? 'total_duration' : 'duration';
      const duration = parseFloat(log[durationField]) || 0;
      return sum + duration;
    }, 0);
  }, [filteredTimeLogs, currentTab]);

  return (
    <StyledTableContainer component={Paper}>
      <Tabs 
        value={currentTab} 
        onChange={handleTabChange} 
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {/* Explicit values keep currentTab's meaning independent of display order. */}
        <Tab label="Action Item Based" value={1} />
        <Tab label="Task Based" value={0} />
        <Tab label="Note Based" value={2} />
        <Tab label="Tracker" value={3} />
      </Tabs>

      {currentTab === 2 && <NoteBasedTimeLog />}
      {currentTab === 3 && <TrackerTimeLog />}
      {currentTab !== 2 && currentTab !== 3 && (
      <>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {currentTab === 1 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
          >
            Add Time Log
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={handleColumnMenuOpen}
        >
          <Settings />
          Columns
        </Button>
        {/* Date Filters */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Date</InputLabel>
          <Select
            value={dateFilter}
            label="Date"
            onChange={e => setDateFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="yesterday">Yesterday</MenuItem>
            <MenuItem value="last_week">Last Week</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
        {dateFilter === 'custom' && (
          <>
            <TextField
              type="date"
              label="From"
              size="small"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="To"
              size="small"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={allWorkspaces}
              onChange={(e) => setAllWorkspaces(e.target.checked)}
            />
          }
          label="All workspaces"
        />
        {/* Epic Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Epic</InputLabel>
          <Select
            value={epicFilter}
            label="Epic"
            onChange={e => {
              setEpicFilter(e.target.value);
              setProjectFilter(''); // Clear project filter when epic changes
              setTrackFilter(''); // Clear track filter when epic changes
            }}
          >
            <MenuItem value="">All</MenuItem>
            {epics.map(epic => (
              <MenuItem key={epic.id} value={epic.id}>{epic.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Project Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Project</InputLabel>
          <Select
            value={projectFilter}
            label="Project"
            onChange={e => {
              setProjectFilter(e.target.value);
              setTrackFilter(''); // Clear track filter when project changes
            }}
          >
            <MenuItem value="">All</MenuItem>
            {projects.map(project => (
              <MenuItem key={project.id} value={project.name}>{project.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Track Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Track</InputLabel>
          <Select
            value={trackFilter}
            label="Track"
            onChange={e => setTrackFilter(e.target.value)}
            disabled={!projectFilter}
          >
            <MenuItem value="">
              {!projectFilter ? "Select project first" : "All"}
            </MenuItem>
            {tracks.map(track => (
              <MenuItem key={track.id} value={track.name}>{track.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleColumnMenuClose}
      >
        <div sx={{ padding: 2 }}>
          {allColumns.map((column) => (
            <div key={column.id}>
              <Checkbox
                checked={visibleColumns.includes(column.id)}
                onChange={() => handleColumnToggle(column.id)}
                id={`column-${column.id}`}
              />
              <label htmlFor={`column-${column.id}`}>
                {column.label}
              </label>
            </div>
          ))}
        </div>
      </Menu>

      <StyledTable>
        <TableHead>
          <TableRow>
            {allColumns
              .filter((column) => visibleColumns.includes(column.id))
              .map((column) => {
                const CellComponent = renderTableCell(column.id);
                return (
                  <CellComponent key={column.id}>{column.label}</CellComponent>
                );
              })}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTimeLogs.map((log, index) => (
            <TableRow key={index}>
              {allColumns
                .filter((column) => visibleColumns.includes(column.id))
                .map((column) => {
                  const CellComponent = renderTableCell(column.id);
                  return (
                    <CellComponent key={column.id}>
                      {renderCell(log, column.id, index)}
                    </CellComponent>
                  );
                })}
            </TableRow>
          ))}
          {filteredTimeLogs.length === 0 && (
            <TableRow>
              <TableCell colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '20px' }}>
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {filteredTimeLogs.length > 0 && (
            <TableRow sx={{ fontWeight: 'bold' }}>
              {allColumns
                .filter((column) => visibleColumns.includes(column.id))
                .map((column) => {
                  const CellComponent = renderTableCell(column.id);
                  return (
                    <CellComponent key={column.id}>
                      {(currentTab === 0 && column.id === 'total_duration') || 
                       (currentTab === 1 && column.id === 'duration') ? (
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatFloatToTimeHM(totalDuration)}
                        </Typography>
                      ) : (currentTab === 0 && column.id === 'name') || 
                          (currentTab === 1 && column.id === 'action_item') ? (
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Total:
                        </Typography>
                      ) : null}
                    </CellComponent>
                  );
                })}
            </TableRow>
          )}
        </TableBody>
      </StyledTable>

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {dialogType === 'action_item'
            ? 'Select Action Item'
            : 'Select Meeting'}
        </DialogTitle>
        <DialogContent>{renderDialogContent()}</DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </StyledTableContainer>
  );
};

export default TimeLog;
